import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import hotelService from '../services/hotelService';
import dvmExtractorService from '../services/dvmExtractorService';
import WizardStepper from './WizardStepper';
import OhipHotelCredentialStep from './OhipHotelCredentialStep';
import './AddPaymentPorterWizard.css';
import './OperaCloudCompare.css';

// Create a DVM Extractor: Details → Hotel & OHIP credential → Format.
//
// An extractor is a saved set-up, not a one-off request — the same property gets extracted repeatedly
// (before and after a config change), and re-entering a credential each time is how the wrong one gets
// used. Extracting itself happens on the detail page.

const STEPS = [
    { num: 1, label: 'Details' },
    { num: 2, label: 'Hotel & credential' },
    { num: 3, label: 'Format' },
];

// What the file comes out as. The list is the lambda's (format.js) — adding one there and here is the
// whole change; nothing in between knows the difference.
const FORMATS = [
    { value: 'xlsx', label: 'Excel (.xlsx)', hint: 'One sheet, one row per mapping. What most people want.' },
    { value: 'csv', label: 'CSV (.csv)', hint: 'Same columns, plain text. Opens in Excel and in anything else.' },
    { value: 'json', label: 'JSON (.json)', hint: 'The rows plus the run’s own details — for feeding another system.' },
];

const DvmExtractorWizard = () => {
    const navigate = useNavigate();
    const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();
    const { isSuperAdmin, isPartner } = useAuth();

    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [externalSystem, setExternalSystem] = useState('');
    const [configuredOnly, setConfiguredOnly] = useState('Y');
    const [format, setFormat] = useState('xlsx');
    const [hotels, setHotels] = useState([]);
    const [hotelData, setHotelData] = useState(null);   // { hotel_id, credential } from step 2
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const loadHotels = useCallback(async () => {
        try {
            const res = await hotelService.getAllHotels(
                selectedAccount ? { accountid: selectedAccount } : { partnerid: selectedPartner }
            );
            setHotels(res.hotels || []);
        } catch (err) {
            console.error('Failed to load hotels', err);
        }
    }, [selectedAccount, selectedPartner]);

    useEffect(() => { loadHotels(); }, [loadHotels]);

    const goToHotel = () => {
        const errs = {};
        if (!name.trim()) errs.name = 'Name is required';
        // NOT required: the credential already carries EXTERNALSYSTEM, and asking someone to retype it
        // produced "Yotel UAT" where OHIP wanted HCONNECT — which it rejects per mapping, so the run
        // failed 118 times with the same sentence.
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setErrors({});
        setStep(2);
    };

    const handleHotelAdd = (data) => {
        setHotelData(data);
        setStep(3);
    };

    const handleSave = async () => {
        if (!hotelData) { setStep(2); return; }
        setSaving(true);
        setSaveError(null);
        try {
            const res = await dvmExtractorService.create({
                name: name.trim(),
                hotel_id: hotelData.hotel_id,
                // The credential's HOTELID override IS the OHIP property code when the credential sits
                // above the hotel; without one the code comes from the credential's own settings.
                ohip_hotel_code: hotelData.credential?.hotel_id_override || null,
                credential_id: hotelData.credential?.credential_id,
                external_system: externalSystem.trim() || null,
                configured_only: configuredOnly,
                format,
            });
            navigate(`/apps/dvm-extractor/${res.dvmextractor.id}`);
        } catch (err) {
            setSaveError(err.response?.data?.error?.message || err.message || 'Could not create the extractor');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ppw-page">
            <WizardStepper steps={STEPS} currentStep={step} />

            {step === 1 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Details</h2>
                        <p className="ppw-card-subtitle">What this extractor is for, and which OHIP interface its mappings belong to.</p>
                    </div>
                    <div className="ppw-card-body">
                        <div className="occ-instance-block">
                            <label className="ppw-label">Name <span className="ppw-required">*</span></label>
                            <input
                                type="text"
                                className="ppw-input"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g. Chewton Glen — OHIP-HCONNECT mappings"
                            />
                            {errors.name && <span className="ppw-error-msg">{errors.name}</span>}
                        </div>

                        <div className="occ-instance-block">
                            <label className="ppw-label">External system <span className="ppw-label-hint">(optional)</span></label>
                            <input
                                type="text"
                                className="ppw-input"
                                value={externalSystem}
                                onChange={e => setExternalSystem(e.target.value)}
                                placeholder="Leave blank to use the credential's own setting"
                            />
                            <span className="ppw-label-hint">
                                The interface CODE configured on the property — HCONNECT, OHIP-HCONNECT — not a display
                                name. Leave it blank and the credential's EXTERNALSYSTEM is used, which is right almost
                                every time; a wrong code is rejected by OHIP with “Interface is not valid”.
                            </span>
                        </div>

                        <div className="occ-instance-block">
                            <label className="ppw-label">Scope</label>
                            <select className="ppw-select" value={configuredOnly} onChange={e => setConfiguredOnly(e.target.value)}>
                                <option value="Y">Configured codes only (faster)</option>
                                <option value="N">Every code in the catalogue</option>
                            </select>
                            <span className="ppw-label-hint">
                                The whole catalogue is one OHIP call per code and mostly empty for a given property.
                            </span>
                        </div>
                    </div>
                    <div className="ppw-footer">
                        <button type="button" className="ppw-btn-back" onClick={() => navigate('/apps/dvm-extractor')}>Cancel</button>
                        <button type="button" className="ppw-btn-next" onClick={goToHotel}>Next</button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <OhipHotelCredentialStep
                    title="Hotel & OHIP credential"
                    subtitle="Choose the property whose mappings are wanted and the OHIP credential used to read them."
                    hotels={hotels}
                    selectedAccount={selectedAccount}
                    selectedPartner={selectedPartner}
                    isSuperAdmin={isSuperAdmin}
                    isPartner={isPartner}
                    isChainAdmin={isChainAdmin}
                    submitLabel="Next"
                    onCancel={() => setStep(1)}
                    onAdd={handleHotelAdd}
                />
            )}

            {step === 3 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Format</h2>
                        <p className="ppw-card-subtitle">How the extracted mappings come back. It can be changed later, or overridden per extract.</p>
                    </div>
                    <div className="ppw-card-body">
                        {FORMATS.map(f => (
                            <label key={f.value} className="occ-instance-block" style={{ display: 'block', cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="dvm-format"
                                    value={f.value}
                                    checked={format === f.value}
                                    onChange={() => setFormat(f.value)}
                                    style={{ marginRight: '0.6rem' }}
                                />
                                <strong>{f.label}</strong>
                                <div className="ppw-label-hint" style={{ marginLeft: '1.6rem' }}>{f.hint}</div>
                            </label>
                        ))}
                        {saveError && <span className="ppw-error-msg" style={{ display: 'block', marginTop: '1rem' }}>{saveError}</span>}
                    </div>
                    <div className="ppw-footer">
                        <button type="button" className="ppw-btn-back" onClick={() => setStep(2)}>Back</button>
                        <button type="button" className="ppw-btn-next" onClick={handleSave} disabled={saving}>
                            {saving ? 'Creating…' : 'Create Extractor'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DvmExtractorWizard;
